"use client";

import React from "react";
import { PostAuthors } from "@/views/post/partials/PostAuthors/PostAuthors";
import { PostBody } from "@/views/post/partials/PostBody/PostBody";
import { TabbedPosts } from "@/views/post/partials/TabbedPosts/TabbedPosts";
import { RelatedMatches } from "@/views/post/partials/RelatedMatches/RelatedMatches";
import { usePostData } from "@/views/post/context/PostDataContext";
import type { LocaleCode } from "@/lib/labels/types";
import { useLabels } from "@/lib/labels/LabelsProvider";
import { useNavigation } from "@/lib/navigation/NavigationProvider";
import { AdSlot } from "@/features/ads/AdSlot";
import { DiscussionWidget } from "@/features/discussion/DiscussionWidget";
import type { Media } from "@/payload-types";
import { VideoEmbed } from "../../partials/VideoEmbed/VideoEmbed";
import { SectionStrip, type BrandedStyle } from "@/components/layout/SectionStrip";
import { VIDEO_HERO } from "@/features/theme/sectionKeys";

/**
 * Standard video layout variant.
 * Layout structure:
 * - Category badge at top
 * - Title below category
 * - Video embed (or fallback hero image) below title
 * - Author and metadata (date)
 * - Video body content
 * - Right sidebar with banner placeholder + tabbed content
 */
interface VideoLayoutStandardProps {
    sectionStyles?: Record<string, BrandedStyle>;
    breadcrumbs?: React.ReactNode;
}

export function VideoLayoutStandard({ sectionStyles, breadcrumbs }: VideoLayoutStandardProps = {}) {
    const { post, locale: localeStr, primaryCategory } = usePostData();
    const locale = localeStr as LocaleCode;
    const labels = useLabels();
    const nav = useNavigation();

    // Format the publication date
    const formattedDate = post.publishedAt
        ? new Date(post.publishedAt).toLocaleDateString(locale, {
              day: "numeric",
              month: "long",
              year: "numeric",
          })
        : null;

    const heroImage = post.heroImage as Media | null;

    // Get category name for display
    const categoryName = primaryCategory?.title;

    return (
        <article className="min-h-screen">
            {/* Main Container */}
            <div className="container mx-auto px-4 py-8 md:py-12 space-y-8">
                <AdSlot id="content-top" />

                <div className="flex flex-col lg:flex-row gap-8">
                    {/* Main Content */}
                    <div className="flex-1 min-w-0 max-w-240">
                        {/* Hero Section */}
                        <SectionStrip brandedStyle={sectionStyles?.[VIDEO_HERO]}>
                            {breadcrumbs ? <div className="mb-6">{breadcrumbs}</div> : null}
                            {/* Category Badge */}
                            {categoryName && (
                                <div className="mb-4">
                                    <span className="inline-block px-3 py-1 text-xs font-medium uppercase tracking-wide bg-primary text-primary-foreground rounded">
                                        {categoryName}
                                    </span>
                                </div>
                            )}

                            {/* Title */}
                            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">{post.title}</h1>

                            {/* Video Embed or Hero Image */}
                            <VideoEmbed
                                videoEmbed={post.videoEmbed}
                                heroImage={heroImage}
                                title={post.title}
                                className="w-full mb-6"
                                heroSize="xlarge"
                                priority
                            />

                            {/* Author and Metadata */}
                            <div className="flex flex-wrap items-center gap-4 text-sm mb-8 pb-6 border-b border-border text-inherit opacity-80">
                                {/* Authors */}
                                {post.populatedAuthors && post.populatedAuthors.length > 0 && (
                                    <div className="flex items-center gap-2">
                                        <span className="font-medium text-inherit opacity-100">
                                            {post.populatedAuthors
                                                .map((author) => author.name)
                                                .filter(Boolean)
                                                .join(", ")}
                                        </span>
                                    </div>
                                )}
                                {/* Date */}
                                {formattedDate && (
                                    <>
                                        <span className="opacity-50">•</span>
                                        <time dateTime={post.publishedAt || undefined}>{formattedDate}</time>
                                    </>
                                )}
                            </div>
                        </SectionStrip>

                        {/* Related Matches - Before Body Content */}
                        <RelatedMatches
                            showHeader={false}
                            matchesLabel={(labels as any).videoPage?.relatedMatches}
                        />

                        {/* Video Body Content */}
                        <PostBody content={post.content} />

                        {/* Authors Section (detailed) */}
                        <PostAuthors
                            authors={post.populatedAuthors}
                            authorLabel={(labels as any).videoPage?.author}
                            authorsLabel={(labels as any).videoPage?.authors}
                        />

                        {/* Comments Section */}
                        <DiscussionWidget
                            entityId={`post-${post.id}`}
                            discussionLabel="Comments"
                            className="mt-8"
                        />
                    </div>

                    {/* Sidebar */}
                    <aside className="w-full lg:min-w-75 lg:w-75 shrink-0">
                        <div className="sticky top-below-nav-gap space-y-6">
                            <AdSlot id="sidebar-top" />

                            {/* Tabbed Related Content (Related Videos + Latest from Category) */}
                            <TabbedPosts
                                variant="default"
                                columns={1}
                                className="pt-0"
                                buildLink={nav.post}
                                relatedLabel={(labels as any).videoPage?.relatedVideos}
                                categoryLabel={(labels as any).videoPage?.latestFromCategory}
                                latestLabel={(labels as any).videoPage?.latestNews}
                            />

                            <AdSlot id="sidebar-bottom" />
                        </div>
                    </aside>
                </div>

                <AdSlot id="content-bottom" />
            </div>
        </article>
    );
}
